import React, { useState } from 'react';
import { OnboardingIntent } from '../../../contexts/OnboardingContext';
import { IntentCard } from '../components/IntentCard';
import { RosaryIcon } from '../../icons/RosaryIcon';

interface IntentDiscoveryScreenProps {
  onContinue: (intent: OnboardingIntent) => void;
}

interface IntentOption {
  id: OnboardingIntent;
  icon: React.ReactNode;
  title: string;
  description: string;
  featured?: boolean;
}

const intentOptions: IntentOption[] = [
  {
    id: '54-day-novena',
    icon: <RosaryIcon size={48} className="text-indigo-600 dark:text-indigo-400" />,
    title: 'Start a 54-day rosary novena',
    description: 'The most powerful novena practice - 27 days of petition followed by 27 days of thanksgiving',
    featured: true,
  },
  {
    id: 'daily-rosary',
    icon: '✝️',
    title: 'Pray the daily rosary with journaling',
    description: 'Build a consistent prayer habit with daily rosary prayers and personal reflections',
  },
  {
    id: 'explore-novenas',
    icon: '🙏',
    title: 'Explore traditional novenas',
    description: 'Discover beautiful 9-day novenas to saints and devotions',
  },
  {
    id: 'browsing',
    icon: '👀',
    title: 'Just browsing prayer options',
    description: 'Take a quick tour of the app without setting up a prayer routine',
  },
];

export const IntentDiscoveryScreen: React.FC<IntentDiscoveryScreenProps> = ({
  onContinue,
}) => {
  const [selectedIntent, setSelectedIntent] = useState<OnboardingIntent | null>(null);

  const handleContinue = () => {
    if (selectedIntent) {
      onContinue(selectedIntent);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          What brings you here today?
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Let's personalize your prayer experience
        </p>
      </div>

      {/* Intent Options */}
      <div className="flex-1 space-y-4 overflow-y-auto pb-2">
        {intentOptions.map((option) => (
          <div key={option.id} className="relative" style={{ marginTop: option.featured ? '16px' : '0' }}>
            {option.featured && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                  ✨ Most Popular
                </span>
              </div>
            )}
            <IntentCard
              icon={option.icon}
              title={option.title}
              description={option.description}
              selected={selectedIntent === option.id}
              onClick={() => setSelectedIntent(option.id)}
            />
          </div>
        ))}
      </div>

      {/* Continue Button */}
      <div className="mt-8">
        <button
          onClick={handleContinue}
          disabled={!selectedIntent}
          className={`
            w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform
            ${
              selectedIntent
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white hover:scale-105 shadow-lg'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
        </button>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
          You can change your focus anytime in settings
        </p>
      </div>
    </div>
  );
};